'use client'

import React, { useState, useEffect } from 'react'
import { X, Printer, Eye } from 'lucide-react'
import { generateLabelPDF, mapEntityToTemplateData, printPDF } from '../../lib/printService'

interface PrintTemplate {
  id: number
  name: string
  width: number
  height: number
  is_default?: boolean
  category_id?: string
}

interface BulkPrintDialogProps {
  isOpen: boolean
  onClose: () => void
  items: any[]
  onPrintComplete?: () => void
}

export default function BulkPrintDialog({ isOpen, onClose, items, onPrintComplete }: BulkPrintDialogProps) {
  const [templates, setTemplates] = useState<PrintTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)

  // Load saved print settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('printSettings')
    if (savedSettings) {
      try {
        const { templateId, printQuantity } = JSON.parse(savedSettings)
        if (printQuantity) setQuantity(printQuantity)
        // Template ID will be set after templates are loaded
      } catch (error) {
        console.error('Error loading saved print settings:', error)
      }
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchTemplates()
    }
  }, [isOpen])

  const fetchTemplates = async () => {
    setIsLoadingTemplates(true)
    try {
      const response = await fetch('/api/templates-pg?scenario=item')
      if (!response.ok) throw new Error('Failed to fetch templates')
      const result = await response.json()
      const data = Array.isArray(result) ? result : (result.data || [])
      setTemplates(data)

      // Load saved template preference or use default
      const savedSettings = localStorage.getItem('printSettings')
      let savedTemplateId: number | null = null

      if (savedSettings) {
        try {
          const { templateId } = JSON.parse(savedSettings)
          savedTemplateId = templateId
        } catch (error) {
          console.error('Error parsing saved settings:', error)
        }
      }

      // Priority: Saved template > Default template > First template
      const savedTemplate = savedTemplateId ? data.find((t: PrintTemplate) => t.id === savedTemplateId) : null
      const defaultTemplate = data.find((t: PrintTemplate) => t.is_default)
      const firstTemplate = data[0]

      setSelectedTemplateId(savedTemplate?.id || defaultTemplate?.id || firstTemplate?.id || null)
    } catch (error) {
      console.error('Error fetching templates:', error)
    } finally {
      setIsLoadingTemplates(false)
    }
  }

  // Save print settings to localStorage when they change
  const handleTemplateChange = (templateId: number) => {
    setSelectedTemplateId(templateId)
    savePrintSettings(templateId, quantity)
  }

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity)
    savePrintSettings(selectedTemplateId, newQuantity)
  }

  const savePrintSettings = (templateId: number | null, printQuantity: number) => {
    try {
      localStorage.setItem('printSettings', JSON.stringify({
        templateId,
        printQuantity,
        lastUpdated: new Date().toISOString()
      }))
    } catch (error) {
      console.error('Error saving print settings:', error)
    }
  }

  const handlePreview = async () => {
    if (!selectedTemplateId || items.length === 0) return

    const template = templates.find(t => t.id === selectedTemplateId)
    if (!template) return

    setIsPrinting(true)
    try {
      // Generate first item's PDF for preview
      const firstItem = items[0]
      const entityData = mapEntityToTemplateData('item', firstItem)
      const pdf = await generateLabelPDF(template, entityData, 1)

      // Open in new window
      window.open(pdf.output('bloburl'), '_blank')
    } catch (error) {
      console.error('Error generating preview:', error)
      alert('Failed to generate preview')
    } finally {
      setIsPrinting(false)
    }
  }

  const handlePrint = async () => {
    if (!selectedTemplateId || items.length === 0) return

    const template = templates.find(t => t.id === selectedTemplateId)
    if (!template) return

    setIsPrinting(true)
    try {
      // Generate combined PDF with all items
      const firstItem = items[0]
      const firstEntityData = mapEntityToTemplateData('item', firstItem)
      const pdf = await generateLabelPDF(template, firstEntityData, quantity)

      // Add remaining items
      for (let i = 1; i < items.length; i++) {
        const entityData = mapEntityToTemplateData('item', items[i])
        const itemPdf = await generateLabelPDF(template, entityData, quantity)

        // Append pages from itemPdf to main pdf
        const pageCount = itemPdf.getNumberOfPages()
        for (let page = 1; page <= pageCount; page++) {
          pdf.addPage()
          const pageContent = itemPdf.internal.pages[page]
          pdf.internal.pages[pdf.internal.pages.length - 1] = pageContent
        }
      }

      // Open print dialog
      printPDF(pdf)

      if (onPrintComplete) {
        onPrintComplete()
      }
      onClose()
    } catch (error) {
      console.error('Error printing labels:', error)
      alert('Failed to print labels')
    } finally {
      setIsPrinting(false)
    }
  }

  if (!isOpen) return null

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId)
  const totalLabels = items.length * quantity

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'linear-gradient(to bottom, #0a0e1a, #1a1f2e)',
          border: '2px solid rgba(0, 240, 255, 0.5)',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 0 40px rgba(0, 240, 255, 0.3)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid rgba(0, 240, 255, 0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1.75rem',
                fontWeight: 700,
                color: '#00F0FF',
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              🖨️ Bulk Print Labels
            </h2>
            <p
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                color: '#8A95AA',
                margin: '8px 0 0 0'
              }}
            >
              {items.length} item{items.length > 1 ? 's' : ''} selected
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '2px solid rgba(0, 240, 255, 0.3)',
              borderRadius: '8px',
              color: '#00F0FF',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.6)'
              e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.3)'
              e.currentTarget.style.background = 'transparent'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Template Selector */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#00F0FF',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                marginBottom: '8px'
              }}
            >
              Label Template
            </label>
            <select
              value={selectedTemplateId || ''}
              onChange={(e) => handleTemplateChange(Number(e.target.value))}
              disabled={isLoadingTemplates}
              style={{
                width: '100%',
                padding: '12px',
                background: '#242938',
                border: '2px solid rgba(0, 240, 255, 0.3)',
                borderRadius: '8px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {isLoadingTemplates ? (
                <option>Loading templates...</option>
              ) : (
                templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.width}" × {template.height}")
                    {template.is_default ? ' ⭐ Default' : ''}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Quantity Per Item */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#00F0FF',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                marginBottom: '8px'
              }}
            >
              Copies Per Item
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={quantity}
              onChange={(e) => handleQuantityChange(Math.max(1, Math.min(100, Number(e.target.value))))}
              style={{
                width: '100%',
                padding: '12px',
                background: '#242938',
                border: '2px solid rgba(0, 240, 255, 0.3)',
                borderRadius: '8px',
                color: '#E0E6F0',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                outline: 'none'
              }}
            />
            <p
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.875rem',
                color: '#8A95AA',
                margin: '8px 0 0 0'
              }}
            >
              Total labels: {totalLabels}
            </p>
          </div>

          {/* Template Info */}
          {selectedTemplate && (
            <div
              style={{
                background: 'rgba(0, 240, 255, 0.05)',
                border: '1px solid rgba(0, 240, 255, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}
            >
              <div
                style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.875rem',
                  color: '#E0E6F0',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px'
                }}
              >
                <div>
                  <span style={{ color: '#8A95AA' }}>Template:</span>{' '}
                  <span style={{ color: '#00F0FF' }}>{selectedTemplate.name}</span>
                </div>
                <div>
                  <span style={{ color: '#8A95AA' }}>Dimensions:</span>{' '}
                  <span style={{ color: '#00F0FF' }}>
                    {selectedTemplate.width}" × {selectedTemplate.height}"
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handlePreview}
              disabled={isPrinting || !selectedTemplateId || items.length === 0}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: '#242938',
                border: '2px solid rgba(0, 240, 255, 0.5)',
                borderRadius: '8px',
                color: '#00F0FF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: isPrinting || !selectedTemplateId ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isPrinting || !selectedTemplateId ? 0.5 : 1,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isPrinting && selectedTemplateId) {
                  e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#242938'
              }}
            >
              <Eye size={18} />
              Preview
            </button>
            <button
              onClick={handlePrint}
              disabled={isPrinting || !selectedTemplateId || items.length === 0}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #00F0FF, #00B8D4)',
                border: '2px solid rgba(0, 240, 255, 0.6)',
                borderRadius: '8px',
                color: '#0a0e1a',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: isPrinting || !selectedTemplateId ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isPrinting || !selectedTemplateId ? 0.5 : 1,
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.4)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isPrinting && selectedTemplateId) {
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 240, 255, 0.6)'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.4)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              <Printer size={18} />
              {isPrinting ? 'Generating...' : `Print ${totalLabels} Label${totalLabels > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
